import type { MessageDescriptor } from 'react-intl';

import { CLI_ROUTE_NAME, CLI_ROUTE_PATH, HOME_ROUTE_NAME, HOME_ROUTE_PATH } from '../../../routes';

import { messages } from './messages';

/**
 * The single data model behind every nav control in this directory: the page
 * links, each carrying the sections of the page it opens. `NavButtons`,
 * `NavItemAnchorMenu` and `NavMenu` (the hamburger) all read from here, so the
 * expanded bar and the collapsed popout can never disagree about what they
 * list.
 *
 * Sections hang off their page rather than being looked up by the current
 * route, which is what lets `NavItemAnchorMenu` offer the home page's sections
 * from every route and label them by where they lead. The hamburger narrows
 * that back to the current route's own sections, so it can head them "On this
 * page".
 */

export type AnchorNavItem = {
	/** Token from `HASH_TO_ANCHOR_ID`. Also the item's `href` fragment. */
	token: string;
	message: MessageDescriptor;
};

export type PageNavItem = {
	/** Route path this item navigates to, and its `href`. */
	path: string;
	/** Route name the current route is matched against for the active state. */
	routeName: string;
	message: MessageDescriptor;
	/**
	 * Sections within this page, in the order they appear down it. Absent for a
	 * page with none, which is what tells every surface to render a plain link
	 * with no disclosure.
	 */
	anchors?: readonly AnchorNavItem[];
};

/**
 * The home page's sections, in the order they appear down the signed-in page:
 * Your Graph, Connectors, Getting started, then that section's MCP install tab.
 * Signed-out visitors receive the same list without Your Graph because their
 * home view has no personal graph section. `about` is deliberately left out
 * because the About page link already goes there. `cli` is left out too: the
 * dedicated CLI page link already covers that destination, so listing `/#cli`
 * next to it would only duplicate the same label.
 *
 * Tokens are keys of `HASH_TO_ANCHOR_ID` (see `common/deep-link/index.ts`).
 */
const HOME_ANCHOR_ITEMS: readonly AnchorNavItem[] = [
	{ token: 'your-graph', message: messages.navYourGraph },
	{ token: 'connectors', message: messages.navConnectors },
	{ token: 'getting-started', message: messages.navGettingStarted },
	{ token: 'mcp', message: messages.navMcp },
];

export const PAGE_LINKS: readonly PageNavItem[] = [
	{
		path: HOME_ROUTE_PATH,
		routeName: HOME_ROUTE_NAME,
		message: messages.navAbout,
		anchors: HOME_ANCHOR_ITEMS,
	},
	// No `anchors`: the CLI page's own sections have no tokens in
	// `HASH_TO_ANCHOR_ID`, so there is nothing to link to yet.
	{ path: CLI_ROUTE_PATH, routeName: CLI_ROUTE_NAME, message: messages.navCli },
];

const LOGGED_OUT_PAGE_LINKS: readonly PageNavItem[] = PAGE_LINKS.map((item) =>
	item.anchors
		? {
				...item,
				anchors: item.anchors.filter(({ token }) => token !== 'your-graph'),
			}
		: item,
);

export const getPageLinks = (isAuthed: boolean): readonly PageNavItem[] =>
	isAuthed ? PAGE_LINKS : LOGGED_OUT_PAGE_LINKS;

/**
 * Every anchor item's `href`: a landing URL on the home page carrying the
 * token as a fragment, valid whether or not the visitor is already there.
 */
export const anchorHref = (token: string): string => `${HOME_ROUTE_PATH}#${token}`;
